import React, { useEffect, useState } from "react";
import { useVaultStore } from "@/stores/vault-store";
import { Link } from "@tanstack/react-router";
import { Button } from "@secure-vault/ui";
import { listVaultFiles, listTrusteeShares } from "@secure-vault/api";
import { FileListItem } from "@secure-vault/api/types";
import { Users, FolderOpen } from "lucide-react";

export function VaultOnboardingContainer() {
  const { isUnlocked } = useVaultStore();

  const [files, setFiles] = useState<FileListItem[]>([]);
  const [trusteesCount, setTrusteesCount] = useState(0);

  useEffect(() => {
    if (!isUnlocked) return;

    const loadData = async () => {
      try {
        const fileList = await listVaultFiles();
        setFiles(fileList);

        const shares = await listTrusteeShares("all");
        setTrusteesCount(shares.length);
      } catch (err) {
        console.error("Failed to load onboarding metrics:", err);
      }
    };

    loadData();
  }, [isUnlocked]);

  // The vault password is no longer demanded on landing. While the vault is
  // locked the metrics effect above no-ops, so the file/trustee counts are
  // hidden (see below) rather than shown as a misleading 0. The owner is
  // prompted for their password only when they act (adding/opening a document),
  // where the master key is actually needed.
  return (
    <div className="mx-auto max-w-6xl space-y-8">
      {/* Header Banner */}
      <div className="bg-card relative overflow-hidden rounded-2xl border p-8 shadow-sm">
        <div className="relative z-10 max-w-2xl space-y-3">
          <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Your vault
          </span>
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Keep your important information safe
          </h1>
          <p className="text-muted-foreground text-base">
            Store the documents your family or representatives may need. You
            control what is added and who can help access it when the time
            comes.
          </p>
        </div>
      </div>

      {/* Main actions */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Documents */}
        <div className="bg-card relative flex flex-col justify-between overflow-hidden rounded-xl border p-8 shadow-sm">
          <div className="relative z-10 space-y-6">
            <div className="bg-muted text-muted-foreground flex h-12 w-12 items-center justify-center rounded-lg border">
              <FolderOpen className="h-6 w-6" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold">Your documents</h3>
              {/* Counts require the master key to read; while locked they'd
                  always be 0, which is misleading — so hide them until unlock. */}
              {isUnlocked && (
                <div className="flex items-baseline gap-2 py-1">
                  <span className="text-3xl font-bold">{files.length}</span>
                  <span className="text-muted-foreground text-sm">
                    {files.length === 1 ? "document" : "documents"} in your
                    vault
                  </span>
                </div>
              )}
              <p className="text-muted-foreground text-sm leading-relaxed">
                Add your will, estate documents, letters of instruction, or
                anything else your family or representatives may need.
              </p>
              <p className="text-muted-foreground/80 text-xs leading-relaxed">
                Your documents are protected so they remain private and can only
                be opened by authorised people.
              </p>
            </div>
          </div>
          <div className="relative z-10 pt-6">
            <Button asChild>
              <Link to="/files">Add or view documents</Link>
            </Button>
          </div>
        </div>

        {/* Trusted people */}
        <div className="bg-card relative flex flex-col justify-between overflow-hidden rounded-xl border p-8 shadow-sm">
          <div className="relative z-10 space-y-6">
            <div className="bg-muted text-muted-foreground flex h-12 w-12 items-center justify-center rounded-lg border">
              <Users className="h-6 w-6" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold">Choose trusted people</h3>
              {isUnlocked && (
                <div className="flex items-baseline gap-2 py-1">
                  <span className="text-3xl font-bold">{trusteesCount}</span>
                  <span className="text-muted-foreground text-sm">
                    {trusteesCount === 1 ? "person" : "people"} chosen
                  </span>
                </div>
              )}
              <p className="text-muted-foreground text-sm leading-relaxed">
                Choose people you trust, such as a family member, close friend,
                solicitor, or executor, to help with your vault when needed.
              </p>
              <p className="text-muted-foreground/80 text-xs leading-relaxed">
                Your trusted people cannot access your vault while you are
                managing it. They can only help open it if your adviser confirms
                that you have died or can no longer manage your affairs.
              </p>
            </div>
          </div>
          <div className="relative z-10 pt-6">
            <Button asChild>
              <Link to="/trustees">Set up trusted people</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
